import { useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useBlogPosts } from '@/hooks/useBlogPosts';
import Navbar from '@/components/feature/Navbar';

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

export default function BlogPostPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { getPostBySlug, getPublishedPosts } = useBlogPosts();
  const post = getPostBySlug(slug || '');
  const allPosts = getPublishedPosts();
  const relatedPosts = allPosts.filter((p) => p.slug !== slug).slice(0, 3);

  useEffect(() => {
    if (post) {
      document.title = post.seoTitle || `${post.title} | AK Energy Blog`;
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) {
        metaDesc.setAttribute('content', post.seoDescription || post.excerpt);
      }
    }
  }, [post]);

  if (!post) {
    return (
      <div className="min-h-screen bg-background-50 pt-32 text-center">
        <Navbar />
        <div className="max-w-xl mx-auto px-6">
          <i className="ri-file-search-line text-5xl text-foreground-300 mb-4 block" />
          <h1 className="font-heading text-2xl font-bold text-foreground-950 mb-3">Article Not Found</h1>
          <p className="text-foreground-600 mb-6">The blog post you are looking for does not exist.</p>
          <Link to="/blog" className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-white rounded-lg font-medium text-sm hover:bg-primary-600 transition-colors">
            <i className="ri-arrow-left-line" /> Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <main className="pt-20 md:pt-24">
        {/* Breadcrumb */}
        <div className="max-w-4xl mx-auto px-4 md:px-6 pt-8 pb-6">
          <nav className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm text-foreground-500">
              <Link to="/" className="hover:text-primary-600 transition-colors">Home</Link>
              <i className="ri-arrow-right-s-line text-xs" />
              <Link to="/blog" className="hover:text-primary-600 transition-colors">Blog</Link>
              <i className="ri-arrow-right-s-line text-xs" />
              <span className="text-foreground-400 truncate max-w-[200px]">{post.title}</span>
            </div>
            <Link
              to="/blog"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-background-200 rounded-lg text-sm font-medium text-foreground-600 hover:bg-background-100 hover:text-foreground-800 transition-colors whitespace-nowrap"
            >
              <i className="ri-arrow-left-line" /> Back to Blog
            </Link>
          </nav>
        </div>

        {/* Article Header */}
        <article className="max-w-4xl mx-auto px-4 md:px-6">
          <div className="mb-6">
            <div className="flex flex-wrap items-center gap-3 mb-5">
              {post.tags.map((tag) => (
                <span key={tag} className="text-xs font-medium text-primary-600 bg-primary-50 px-3 py-1.5 rounded-full">
                  {tag}
                </span>
              ))}
            </div>
            <h1 className="font-heading text-2xl md:text-4xl lg:text-5xl font-bold text-foreground-950 mb-5 leading-tight">
              {post.title}
            </h1>
            <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500 mb-8">
              <span className="flex items-center gap-1.5">
                <i className="ri-user-line" />
                {post.author}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line" />
                {formatDate(post.publishedAt)}
              </span>
              {post.updatedAt !== post.publishedAt && (
                <span className="flex items-center gap-1.5">
                  <i className="ri-edit-line" />
                  Updated {formatDate(post.updatedAt)}
                </span>
              )}
            </div>
          </div>

          {/* Cover Image */}
          <div className="rounded-2xl overflow-hidden mb-10">
            <img
              src={post.coverImage}
              alt={post.title}
              className="w-full h-auto max-h-[500px] object-cover"
            />
          </div>

          {/* Content */}
          <div
            className="prose prose-lg max-w-none prose-headings:font-heading prose-headings:text-foreground-950 prose-p:text-foreground-700 prose-a:text-primary-600 prose-strong:text-foreground-900 prose-blockquote:border-l-4 prose-blockquote:border-secondary-300 prose-blockquote:bg-secondary-50/50 prose-blockquote:py-2 prose-blockquote:pl-4 prose-blockquote:rounded-r prose-ul:list-disc prose-ul:pl-5 prose-ol:list-decimal prose-ol:pl-5 mb-16"
            dangerouslySetInnerHTML={{ __html: post.content }}
          />

          {/* Share */}
          <div className="flex items-center gap-3 py-6 border-t border-background-200 mb-16">
            <span className="text-sm font-medium text-foreground-600">Share:</span>
            <button
              onClick={() => {
                const url = window.location.href;
                navigator.clipboard.writeText(url).then(() => alert('Link copied to clipboard!'));
              }}
              className="w-9 h-9 rounded-full bg-background-100 hover:bg-background-200 flex items-center justify-center text-foreground-600 transition-colors cursor-pointer"
              aria-label="Copy link"
            >
              <i className="ri-link" />
            </button>
            <a
              href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(window.location.href)}&text=${encodeURIComponent(post.title)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="w-9 h-9 rounded-full bg-background-100 hover:bg-background-200 flex items-center justify-center text-foreground-600 transition-colors"
              aria-label="Share on Twitter"
            >
              <i className="ri-twitter-x-fill" />
            </a>
            <a
              href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="w-9 h-9 rounded-full bg-background-100 hover:bg-background-200 flex items-center justify-center text-foreground-600 transition-colors"
              aria-label="Share on LinkedIn"
            >
              <i className="ri-linkedin-fill" />
            </a>
          </div>
        </article>

        {/* Related Posts */}
        {relatedPosts.length > 0 && (
          <section className="bg-background-100/50 py-16 md:py-20">
            <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-10">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-8">Related Articles</h2>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {relatedPosts.map((rp) => (
                  <Link
                    key={rp.id}
                    to={`/blog/${rp.slug}`}
                    className="group bg-white rounded-xl border border-background-200 overflow-hidden hover:border-primary-200 transition-all duration-300 hover:shadow-sm flex flex-col"
                  >
                    <div className="h-44 overflow-hidden">
                      <img
                        src={rp.coverImage}
                        alt={rp.title}
                        className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-500"
                      />
                    </div>
                    <div className="p-5 flex flex-col flex-1">
                      <h3 className="font-heading text-base font-bold text-foreground-950 mb-2 group-hover:text-primary-700 transition-colors leading-snug flex-1">
                        {rp.title}
                      </h3>
                      <span className="text-xs text-foreground-400 mt-2">{formatDate(rp.publishedAt)}</span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Schema.org Article structured data */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'BlogPosting',
              headline: post.title,
              description: post.excerpt,
              url: `https://akenergy.com.au/blog/${post.slug}`,
              datePublished: post.publishedAt,
              dateModified: post.updatedAt,
              author: {
                '@type': 'Organization',
                name: post.author,
              },
              publisher: {
                '@type': 'Organization',
                name: 'AK Energy',
                logo: {
                  '@type': 'ImageObject',
                  url: 'https://static.readdy.ai/image/ff63a95a3d068db59710e28833339873/473109dd943942010dc73e384d113dfa.png',
                },
              },
              image: post.coverImage,
              mainEntityOfPage: {
                '@type': 'WebPage',
                '@id': `https://akenergy.com.au/blog/${post.slug}`,
              },
            }),
          }}
        />
      </main>
    </div>
  );
}